/**
 * The application's single frame loop.
 *
 * Everything that wants to repaint calls `invalidate()`; the scheduler coalesces those
 * calls into one `requestAnimationFrame` and runs the frame callback once. Painting
 * synchronously from an event handler — the obvious alternative — repaints several times
 * per input event during a drag and makes the frame budget impossible to reason about.
 *
 * `requestAnimationFrame` is injected rather than reached for globally so the loop can be
 * driven deterministically in tests: a frame loop that can only be tested by waiting for
 * real animation frames is a frame loop that will not be tested.
 */

export interface FrameInfo {
  /** Milliseconds since the previous frame. `0` on the first frame. */
  readonly delta: number;
  /** Timestamp handed over by the animation frame callback. */
  readonly timestamp: number;
  /** Frames rendered since the scheduler started. */
  readonly frame: number;
}

export type FrameCallback = (info: FrameInfo) => void;

export interface SchedulerHost {
  requestAnimationFrame(callback: (timestamp: number) => void): number;
  cancelAnimationFrame(handle: number): void;
}

/**
 * How many frames in a row may throw before the loop gives up.
 *
 * One failed frame is a transient — a value that was momentarily degenerate, a font not yet
 * loaded — and retrying is right. A painter that throws every time is a defect, and retrying
 * it sixty times a second turns one bug into a frozen tab and an unreadable console.
 */
const CONSECUTIVE_FAILURES_ALLOWED = 5;

function defaultHost(): SchedulerHost {
  return {
    requestAnimationFrame: (callback) => globalThis.requestAnimationFrame(callback),
    cancelAnimationFrame: (handle) => {
      globalThis.cancelAnimationFrame(handle);
    },
  };
}

export class RenderScheduler {
  private handle: number | null = null;
  private lastTimestamp = 0;
  private frameCount = 0;
  private running = false;

  /**
   * Set while a frame callback is executing.
   *
   * An `invalidate()` raised *during* a frame — a painter discovering more work, a
   * measurement resizing its own label — must schedule the *next* frame rather than be
   * swallowed by the flag that says a frame is already pending.
   */
  private invalidatedDuringFrame = false;

  private readonly host: SchedulerHost;
  private readonly onFrame: FrameCallback;
  private readonly onError: (error: unknown) => void;

  /** Consecutive frames that have thrown. Reset by any frame that completes. */
  private failures = 0;
  private surrendered = false;

  constructor(onFrame: FrameCallback, host?: SchedulerHost, onError?: (error: unknown) => void) {
    this.onFrame = onFrame;
    this.host = host ?? defaultHost();
    this.onError = onError ?? (() => undefined);
  }

  /**
   * Whether the loop has stopped after repeated failures.
   *
   * Exposed so the shell can say so. A frozen canvas that still accepts clicks is the worst
   * way to fail: the user keeps working, sees nothing change, and loses the lot.
   */
  get hasSurrendered(): boolean {
    return this.surrendered;
  }

  get isPending(): boolean {
    return this.handle !== null;
  }

  get frames(): number {
    return this.frameCount;
  }

  /** Requests a frame. Repeated calls before the frame runs collapse into one. */
  invalidate(): void {
    if (this.surrendered) return;
    if (this.running) {
      this.invalidatedDuringFrame = true;
      return;
    }
    if (this.handle !== null) return;
    this.handle = this.host.requestAnimationFrame(this.tick);
  }

  /** Cancels any pending frame. */
  cancel(): void {
    if (this.handle === null) return;
    this.host.cancelAnimationFrame(this.handle);
    this.handle = null;
  }

  dispose(): void {
    this.cancel();
  }

  private readonly tick = (timestamp: number): void => {
    this.handle = null;
    this.running = true;
    this.invalidatedDuringFrame = false;

    const delta = this.frameCount === 0 ? 0 : timestamp - this.lastTimestamp;
    this.lastTimestamp = timestamp;
    this.frameCount += 1;

    /*
     * A frame that throws must not take the loop down with it.
     *
     * Resetting `running` in a `finally` was not enough, and the gap was easy to miss: the
     * error still escaped into the animation-frame callback, so the rescheduling below never
     * ran. The canvas then froze permanently while the rest of the application carried on
     * accepting clicks — the user keeps drawing into a picture that stopped updating, and
     * only finds out when they look away and back.
     *
     * So the error is caught, reported, and the loop continues. Continuing is right because
     * the usual cause is a value that is momentarily degenerate mid-drag and defined again by
     * the next move.
     */
    let threw = false;
    try {
      this.onFrame({ delta, timestamp, frame: this.frameCount });
    } catch (error) {
      threw = true;
      this.failures += 1;
      this.onError(error);
    } finally {
      this.running = false;
    }

    if (!threw) this.failures = 0;

    if (this.failures >= CONSECUTIVE_FAILURES_ALLOWED) {
      this.surrendered = true;
      this.consumeDeferredInvalidation();
      return;
    }

    // A failed frame reschedules itself: whatever it was going to paint, it did not.
    if (this.consumeDeferredInvalidation() || threw) this.invalidate();
  };

  /**
   * Reads and clears the mid-frame invalidation flag.
   *
   * Extracted into a method rather than inlined because control-flow analysis narrows
   * `this.invalidatedDuringFrame` to `false` after the assignment at the top of `tick`
   * — it cannot see that the `onFrame` callback may set it. Reading through a method
   * call restores the honest `boolean`.
   */
  private consumeDeferredInvalidation(): boolean {
    const deferred = this.invalidatedDuringFrame;
    this.invalidatedDuringFrame = false;
    return deferred;
  }
}
